import React from "react";
import {
  Brain, Sparkles, Palette, Globe, MessageSquare, BarChart3, Check,
} from "lucide-react";
import AIHeroVisual from "@/components/AIHeroVisual";

const FEATURES = [
  { icon: Brain, title: "AI Business Analysis", desc: "Deep SWOT, customer personas, marketing strategy and a 90-day roadmap — generated in seconds." },
  { icon: Sparkles, title: "AI Content Studio", desc: "Auto-write posts for Instagram, Facebook, LinkedIn, X and full-length SEO blogs." },
  { icon: Palette, title: "AI Brand Identity", desc: "Generate logos, color palettes, typography, brand voice & a complete style guide." },
  { icon: Globe, title: "AI Website Builder", desc: "Spin up multi-page websites with SEO, schema and copy — ready to publish." },
  { icon: MessageSquare, title: "AI Engagement", desc: "Reply to reviews, comments and DMs with on-brand voice. Sensitive issues escalated." },
  { icon: BarChart3, title: "AI Analytics & Growth", desc: "Track reach, engagement, leads and conversions. AI suggests what to ship next." },
];

const PROMISES = [
  "AI never auto-publishes sensitive or legal content",
  "Audit logs for every action taken on your behalf",
  "Encrypted tokens for every connected social account",
  "GDPR-ready. Cookie consent. Full data export.",
  "Approval workflows you can switch on or off per channel",
];

export default function FeatureGrid() {
  return (
    <>
      <section id="features" className="max-w-7xl mx-auto px-6 lg:px-8 py-28">
        <div className="grid lg:grid-cols-12 gap-12 mb-16" data-reveal>
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-4">The platform</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">
              One brain. <br /><span className="shimmer-text">Twenty marketing roles.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-8">
            <p className="text-lg text-white/60 leading-relaxed">
              MakeYourBrand.IN is a single platform that replaces your strategist, designer, copywriter,
              SEO lead, ads manager, social media manager, community manager and analyst. It works 24/7,
              speaks your brand voice and respects every approval rule you set.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              data-reveal
              style={{ transitionDelay: `${i * 80}ms` }}
              className="glass rounded-2xl p-7 tilt-card hover:border-indigo-500/30"
              data-testid={`feature-card-${i}`}
            >
              <div className="h-10 w-10 rounded-lg brand-gradient grid place-items-center mb-5 animate-ai-pulse">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="font-heading font-bold text-xl tracking-tight mb-2">{f.title}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 relative" data-reveal>
            <div className="absolute -inset-10 brand-gradient opacity-20 blur-3xl rounded-full" />
            <div className="relative glass-strong rounded-3xl p-8 border border-indigo-500/20 overflow-hidden">
              <AIHeroVisual />
            </div>
          </div>
          <div className="lg:col-span-6" data-reveal>
            <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-4">Built for autonomy</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-6">
              AI-first. <span className="shimmer-text">Human-approved when it counts.</span>
            </h2>
            <ul className="space-y-3 text-white/70">
              {PROMISES.map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-indigo-400 shrink-0 mt-0.5" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </>
  );
}
